import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "./roleCreationPage.css";
import "../0PageStyles.css";

//Components
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Button from "../../Components/Other Components/button";
import PageLoader from "../../Components/Other Components/pageLoader";
import PermissionView from "../../Components/User Components/PermissionView";
import WarehousePicker from "../../Components/Warehouse Components/warehousePicker";
import Title from "../../Components/Other Components/Title";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

//Functions and hooks
import { CreateRole, GetNextRoleID } from "../../BackendAccess/RoleHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useTranslation } from "react-i18next";

function RoleCreationPage() {
  const [ID, setID] = useState();
  const [name, setName] = useState();
  const [priorityLevel, setPriorityLevel] = useState();
  const [color, setColor] = useState("hsl(0,0%,100%)");
  const [permissions, setPermissions] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(false);

  const [createBtnDisabled, setCreateBtnDisabled] = useState(false);

  const navigate = useNavigate();

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    const fetchInitialValues = async () => {
      const [data, error] = await fetchWithTimeout(GetNextRoleID);
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setID(data);
      }

      setLoading(false);
    };
    fetchInitialValues();
  }, []);

  const handleIDChange = (e) => {
    const value = e;

    setID(value);
  };
  const handleNameChange = (e) => {
    const value = e;
    setName(value);
  };
  const handlePriorityLevelChange = (e) => {
    const value = e;
    if (/^\d*$/.test(value)) {
      setPriorityLevel(value);
    } else {
      setError({
        err: t("validation.The Priority Level must be a positive number.", {
          ns: "error",
        }),
        errCode: 400,
      });
    }
  };
  const handleColorChange = (e) => {
    setColor(e);
  };

  const OnCreate = async () => {
    setCreateBtnDisabled(true);
    showNotification(t("Creating role...", { ns: "roles" }), "info");
    try {
      const permissionsToPass = [...permissions].filter(
        (item) => item.HasPermission
      );
      console.log(permissions);

      const [data, error] = await CreateRole(
        ID,
        name,
        priorityLevel,
        color,
        permissionsToPass,
        warehouses
      );
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Role created successfully!", { ns: "roles" }),
          "success"
        );
        navigate(-1);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setCreateBtnDisabled(false);
    }
  };

  const clickOnCancel = () => {
    showNotification(t("Role creation canceled.", { ns: "roles" }), "info");
    navigate(-1);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Creating a new role", { ns: "tutorial" }),
      message: t(
        "This page creates a new role - a bundle of permissions you can assign to any number of employees, controlling exactly what they can see and do.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-creation-name",
      title: t("Name", { ns: "tutorial" }),
      message: t(
        "Give the role a name that describes what it's for, like \"Warehouse Manager\" or \"Sales Employee\".",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-creation-priority",
      title: t("Priority level", { ns: "tutorial" }),
      message: t(
        "Controls which roles this role is allowed to edit. Lower numbers are higher priority - a role can only edit roles with a lower priority (higher number) than its own.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-creation-color",
      title: t("Color", { ns: "tutorial" }),
      message: t(
        "Pick a color for this role, shown next to it wherever roles are listed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-creation-permissions",
      title: t("Permissions", { ns: "tutorial" }),
      message: t(
        "Tick every action this role should be allowed to perform - creating items, viewing traffic, managing employees, and so on. Nothing is granted by default.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-creation-warehouses",
      title: t("Allowed warehouses", { ns: "tutorial" }),
      message: t(
        "Optional. Restrict this role to only see and work with specific warehouses. Leave empty to allow all warehouses.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "role-creation-create-btn",
      title: t("Save the role", { ns: "tutorial" }),
      message: t(
        "Once everything looks right, click here to create the role. You can then assign it to employees from the Employees page.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="roleCreation" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <InputGroup className="container-bordered role-creation w-90 vertical mb-5 mt-4">
            <InputField
              placeholder={t("Example: 1001")}
              label={t("ID") + ":"}
              value={ID}
              onChange={handleIDChange}
              className="mb-2"
              mandatory={true}
              readOnly={true}
            />

            <InputField
              placeholder={t("Example: Manager", { ns: "roles" })}
              label={t("Name") + ":"}
              value={name}
              onChange={handleNameChange}
              className="mb-2"
              mandatory={true}
              tutorialId="role-creation-name"
            />

            <InputField
              placeholder={t("Example: 1", { ns: "roles" })}
              label={t("Priority level") + ":"}
              value={priorityLevel}
              onChange={handlePriorityLevelChange}
              className="mb-2"
              description={t(
                "The priority level of a role determines which roles you can edit. The highest priority level is 0.",
                { ns: "roles" }
              )}
              mandatory={true}
              tutorialId="role-creation-priority"
            />
            <InputField
              label={t("Color") + ":"}
              type={"color"}
              value={color}
              onChange={handleColorChange}
              tutorialId="role-creation-color"
            />
            <MandatoryExplanation />
          </InputGroup>

          <Title className="mt-4">Permissions</Title>
          <div data-tutorial-id="role-creation-permissions">
            <PermissionView
              getPermissionsForUser={false}
              selectable={true}
              setNewSelection={setPermissions}
            />
          </div>

          <div data-tutorial-id="role-creation-warehouses">
            <WarehousePicker className={"w-90 mt-5"} onChange={setWarehouses} />
          </div>

          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-positive"
              onClick={OnCreate}
              disabled={createBtnDisabled}
              acceptEnter={true}
              icon={"create"}
              tutorialId="role-creation-create-btn"
            >
              {t("Create")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}

export default RoleCreationPage;
